import { Check, Truck } from 'lucide-react';
import DestinationChips from './DestinationChips';
import { lineNotices } from './Tags';
import ProductImage from './ProductImage';
import { KINDS } from '../lib/verdict';
import { boxDims, boxLabel, isMultiBox } from '../lib/boxes';
import { cn, fmtVolume, fmtWeight, qty } from '../lib/utils';

/* One line of the receipt, as a card in three bands so a glance answers
   "what is it, where does it go, how many" and only a second look reads
   the numbers:
   - head: image, name, the codes printed on the label, destinations, count
   - facts strip (sunken): box size, line volume, shipped; the boxes of a
     multi-box product
   - notice band (amber): only when something needs attention */
export default function LineRow({ line, count = 0, boxCounts = {}, dest, onOpen, showPo = false }) {
  const done = line.status === 'done';
  const expected = line.to_receive;
  const complete = !done && expected > 0 && count >= expected;
  const over = !done && count > expected;
  const multi = isMultiBox(line);
  const tone = done ? '' : over ? KINDS.over.tone : dest ? KINDS[dest.primary]?.tone : '';

  const facts = [];
  if (!multi && line.boxes[0] && boxDims(line.boxes[0])) facts.push(['Box', boxDims(line.boxes[0])]);
  if (multi) facts.push(['Unit', `${line.volume_l ? `${fmtVolume(line.volume_l)}${line.weight_kg ? ` · ${fmtWeight(line.weight_kg)}` : ''} · ` : ''}${line.boxes.length} boxes`]);
  if (!done && line.to_receive > 1 && line.line_volume_l) facts.push(['Line', `${fmtVolume(line.line_volume_l)}${line.line_weight_kg ? ` · ${fmtWeight(line.line_weight_kg)}` : ''}`]);
  if (line.shipped_pending) facts.push(['Shipped', qty(line.shipped)]);
  const notices = done ? [] : lineNotices(line);
  const codes = [line.vendor_code, !multi && line.boxes[0]?.vendor_barcode && `EAN ${line.boxes[0].vendor_barcode}`].filter(Boolean);

  return (
    <button
      type="button"
      onClick={() => onOpen?.(line)}
      className={cn(tone, 'relative w-full overflow-hidden rounded-xl bg-surface text-left active:bg-sunken', done && 'opacity-55', complete && !over && 'row-done')}
      key={complete ? 'done' : 'open'}
    >
      <span className={cn('absolute bottom-2 left-0 top-2 w-1 rounded-r-full', done ? 'bg-line' : dest === undefined ? 'skeleton' : 'bg-tone')} />

      {/* head */}
      <span className="flex items-start gap-3 py-2.5 pl-4 pr-3">
        <ProductImage src={line.image} alt="" className="h-14 w-14" />
        <span className="min-w-0 flex-1 py-0.5">
          <span className="clamp-2 block text-[14px] font-semibold leading-tight">{line.name}</span>
          {codes.length > 0 && <span className="mt-0.5 block truncate font-mono text-[12px] leading-tight tabular-nums text-ink2">{codes.join(' · ')}</span>}
          {!done && <DestinationChips dest={dest} className="mt-1.5" />}
        </span>
        <span className="flex shrink-0 flex-col items-end pt-0.5">
          {/* the PO number only: a long name ("P33455 - Allia Anvers…") squeezed the product name to nothing */}
          {showPo && line.po && <span title={line.po.name} className="font-cond mb-0.5 max-w-[5.5rem] truncate rounded-md bg-sunken px-1.5 py-0.5 text-[12px] font-bold leading-none text-ink">{line.po.name.split(' ')[0]}</span>}
          {done ? (
            <span className="inline-flex items-center gap-1 text-[13px] font-semibold text-ink2">
              <Check className="h-4 w-4" /> {qty(line.received)}
            </span>
          ) : (
            <>
              <span className={cn('font-cond text-[28px] font-bold leading-none', complete && !over && 'text-out', over && 'text-alert', count === 0 && 'text-ink2')}>
                <span key={count} className={count > 0 ? 'bump' : undefined}>{qty(count)}</span>
                <span className="text-[18px] font-semibold text-ink2"> / {qty(expected)}</span>
              </span>
              {complete && !over && <Check className="mt-1 h-4 w-4 text-out" />}
            </>
          )}
        </span>
      </span>

      {/* facts strip */}
      {!done && (facts.length > 0 || multi) && (
        <span className="block border-t border-line bg-sunken py-1.5 pl-4 pr-3">
          {facts.length > 0 && (
            <span className="flex flex-wrap gap-x-4 gap-y-0.5">
              {facts.map(([label, value]) => (
                <span key={label} className="inline-flex items-baseline gap-1.5 text-[12px] text-ink">
                  <span className="text-[11px] font-bold uppercase tracking-wide text-ink2">{label}</span>
                  {label === 'Shipped' && <Truck className="h-3.5 w-3.5 self-center text-ink2" />}
                  {value}
                </span>
              ))}
            </span>
          )}
          {multi && (
            <ul className={cn('space-y-0.5', facts.length > 0 && 'mt-1.5')}>
              {line.boxes.map((b, i) => {
                const c = Number(boxCounts[b.id] || 0);
                const full = c >= expected;
                const extra = c > expected;
                const partial = c > 0 && !full;
                return (
                  <li key={b.id} className={cn('flex items-center gap-2 py-0.5 text-[13px] leading-tight', full ? 'text-ink' : 'text-ink2')}>
                    <span
                      key={`${b.id}-${full}`}
                      className={cn(
                        'flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2',
                        full && 'pop',
                        full && !extra && 'tone-out border-tone bg-tone text-bg',
                        extra && 'tone-over border-tone bg-tone text-bg',
                        partial && 'border-ink text-ink',
                        c === 0 && 'border-line'
                      )}
                      aria-label={full ? 'scanned' : 'not scanned'}
                    >
                      {full ? <Check className="h-3.5 w-3.5" strokeWidth={3} /> : partial ? <span className="text-[11px] font-bold">{qty(c)}</span> : null}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-baseline gap-2">
                        <span className={cn('shrink-0 font-semibold', !full && 'text-ink')}>{boxLabel(b, i)}</span>
                        <span className="min-w-0 truncate">{boxDims(b) || ''}</span>
                      </span>
                      <span className="block truncate font-mono text-[11px] leading-tight tabular-nums text-ink2">{b.vendor_barcode || 'no vendor EAN'}</span>
                    </span>
                    <span className="font-cond shrink-0 text-[16px] font-bold tabular-nums">
                      {qty(c)}
                      <span className="text-[13px] font-semibold text-ink2">/{qty(expected)}</span>
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </span>
      )}

      {/* notice band */}
      {notices.length > 0 && (
        <span className="tone-crossdock flex flex-wrap items-center gap-x-3 gap-y-0.5 border-t border-line bg-tone-bg py-1.5 pl-4 pr-3 text-[12px] font-semibold text-tone">
          {notices.map((n) => (
            <span key={n.key} className={cn('inline-flex items-center gap-1', n.tone === 'alert' && 'text-alert', n.tone === 'ink' && 'text-ink')}>
              {n.icon && <n.icon className="h-3.5 w-3.5" />}
              {n.text}
            </span>
          ))}
        </span>
      )}
    </button>
  );
}
